import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getTodayOutlet, formatDate, WEEK_FOCUSES } from '../utils/constants';
import { Clock, TrendingUp, BookOpen, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function SkeletonBox({ className = '' }) {
  return <div className={`bg-muted rounded-md animate-pulse ${className}`} />;
}

function DashboardSkeleton() {
  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-12">
        <SkeletonBox className="h-12 w-56 mb-3" />
        <SkeletonBox className="h-5 w-40" />
      </div>
      <SkeletonBox className="h-28 mb-8 rounded-sm" />
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
        <div className="md:col-span-8"><SkeletonBox className="h-44 rounded-sm" /></div>
        <div className="md:col-span-4"><SkeletonBox className="h-44 rounded-sm" /></div>
        <div className="md:col-span-4"><SkeletonBox className="h-28 rounded-sm" /></div>
        <div className="md:col-span-4"><SkeletonBox className="h-28 rounded-sm" /></div>
        <div className="md:col-span-4"><SkeletonBox className="h-28 rounded-sm" /></div>
      </div>
    </div>
  );
}

function ScoreBar({ label, value, max = 10 }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-muted-foreground font-['IBM_Plex_Sans'] w-20 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-foreground/70 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-medium font-['IBM_Plex_Sans'] w-8 text-right">{value}/{max}</span>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [todayEntry, setTodayEntry] = useState(null);
  const navigate = useNavigate();
  const todayOutlet = getTodayOutlet();
  const today = formatDate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, settingsRes, entriesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/stats/overview`),
        axios.get(`${BACKEND_URL}/api/settings`),
        axios.get(`${BACKEND_URL}/api/daily/entries?date=${today}`)
      ]);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      if (entriesRes.data.length > 0) {
        setTodayEntry(entriesRes.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  if (!stats || !settings) {
    return <DashboardSkeleton />;
  }

  const weekInfo = WEEK_FOCUSES[settings?.current_week || 1];
  const completionRate = todayEntry ? (todayEntry.tasks_completed.length / 6) * 100 : 0;

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 sm:mb-12">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-2">{getGreeting()}</h1>
        <p className="text-lg text-muted-foreground font-['IBM_Plex_Sans']">
          Week {settings.current_week} · {weekInfo.focus}
        </p>
      </div>

      {/* Today's Outlet Banner */}
      {todayOutlet && (
        <div
          data-testid="today-outlet-banner"
          className="mb-8 p-4 sm:p-8 bg-primary/5 border-l-4 border-primary rounded-sm"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-['IBM_Plex_Sans']">
                Today's Outlet
              </p>
              <h2 className="text-xl sm:text-3xl font-bold tracking-tight mb-2">{todayOutlet.name}</h2>
              <p className="text-muted-foreground font-['IBM_Plex_Sans']">
                Focus: {todayOutlet.focus}
              </p>
            </div>
            <Button
              data-testid="btn-start-today"
              onClick={() => navigate('/workspace')}
              className="uppercase tracking-wide font-medium"
            >
              Start Writing
            </Button>
          </div>
        </div>
      )}

      {/* Bento Grid Stats */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
        {/* Today's Progress - Large */}
        <div className="md:col-span-8 bg-card border border-border p-4 sm:p-8 rounded-sm hover:shadow-sm transition-shadow">
          <div className="flex items-center gap-2 mb-6">
            <Clock className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-bold tracking-tight">Today's Progress</h3>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-['IBM_Plex_Sans'] text-muted-foreground">
                  Tasks Completed
                </span>
                <span className="text-sm font-medium font-['IBM_Plex_Sans']">
                  {todayEntry?.tasks_completed.length || 0} / 6
                </span>
              </div>
              <Progress value={completionRate} className="h-2" />
            </div>

            <div className="pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground font-['IBM_Plex_Sans'] mb-3">
                {weekInfo.description}
              </p>
              <div className="flex gap-3">
                <Button
                  data-testid="btn-view-tasks"
                  onClick={() => navigate('/tasks')}
                  variant="outline"
                  size="sm"
                  className="uppercase tracking-wide text-xs"
                >
                  View Tasks
                </Button>
                <Button
                  data-testid="btn-log-progress"
                  onClick={() => navigate('/progress')}
                  variant="outline"
                  size="sm"
                  className="uppercase tracking-wide text-xs"
                >
                  Log Score
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Avg Score */}
        <div className="md:col-span-4 bg-card border border-border p-6 rounded-sm hover:shadow-sm transition-shadow">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-bold uppercase tracking-widest font-['IBM_Plex_Sans']">
              Avg Score
            </h3>
          </div>
          <p className="text-4xl font-bold tracking-tight mb-1">
            {stats.avg_score}<span className="text-2xl text-muted-foreground">/40</span>
          </p>
          <p className="text-xs text-muted-foreground font-['IBM_Plex_Sans']">
            Target: 32+ (passing)
          </p>
        </div>

        {/* Total Days */}
        <div className="md:col-span-4 bg-card border border-border p-6 rounded-sm hover:shadow-sm transition-shadow">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-bold uppercase tracking-widest font-['IBM_Plex_Sans']">
              Days Trained
            </h3>
          </div>
          <p className="text-4xl font-bold tracking-tight mb-1">{stats.total_days}</p>
          <p className="text-xs text-muted-foreground font-['IBM_Plex_Sans']">Keep the streak going</p>
        </div>

        {/* Clips Saved */}
        <div className="md:col-span-4 bg-card border border-border p-6 rounded-sm hover:shadow-sm transition-shadow">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-bold uppercase tracking-widest font-['IBM_Plex_Sans']">
              Clips Saved
            </h3>
          </div>
          <p className="text-4xl font-bold tracking-tight mb-1">{stats.clips_saved}</p>
          <p className="text-xs text-muted-foreground font-['IBM_Plex_Sans']">Building your portfolio</p>
        </div>

        {/* Tasks Done */}
        <div className="md:col-span-4 bg-card border border-border p-6 rounded-sm hover:shadow-sm transition-shadow">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-bold uppercase tracking-widest font-['IBM_Plex_Sans']">
              Tasks Done
            </h3>
          </div>
          <p className="text-4xl font-bold tracking-tight mb-1">{stats.tasks_completed}</p>
          <p className="text-xs text-muted-foreground font-['IBM_Plex_Sans']">Across all sessions</p>
        </div>
      </div>

      {/* Recent Scores */}
      {stats.recent_scores && stats.recent_scores.length > 0 && (
        <div className="bg-card border border-border p-8 rounded-sm">
          <h3 className="text-lg font-bold tracking-tight mb-6">Recent Scores</h3>
          <div className="space-y-6">
            {stats.recent_scores.map((score, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium font-['IBM_Plex_Sans']">{score.date}</span>
                  <span className="text-sm font-bold font-['IBM_Plex_Sans']">
                    {score.total_score}
                    <span className="text-muted-foreground font-normal">/40</span>
                  </span>
                </div>
                <div className="space-y-1.5">
                  <ScoreBar label="Lede" value={score.lede_score} />
                  <ScoreBar label="Nut graf" value={score.nut_graf_score} />
                  <ScoreBar label="Style" value={score.style_score} />
                  <ScoreBar label="Fact-check" value={score.fact_check_score} />
                </div>
                {index < stats.recent_scores.length - 1 && (
                  <div className="mt-5 border-t border-border" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
